import { useCallback, useEffect } from "react";
import { supabase } from "../../../supabaseClient";
import { sortTeamAssignments } from "../../../utility/teamAssignments";
import { fetchEventsBySquad, withSquadEventIds } from "../../../utility/squadEventNames";
import { listBracketProducts, listSquadTickets } from "../../../utility/brackets/api";
import {
  countsFromTickets,
  productsEnabledForSquad,
} from "../../../utility/brackets/entryTickets";

export const useTeamEditData = (
  tournamentId,
  teamId,
  setLoading,
  setTeam,
  setSquads,
  setTournamentEvents,
  setFormData
) => {
  const loadTeam = useCallback(
    async ({ silent = false } = {}) => {
      if (!teamId || !tournamentId) {
        console.error("Missing teamId or tournamentId");
        setLoading(false);
        return;
      }

      if (!silent) {
        setLoading(true);
      }

      try {
        // Fetch team details with bowlers
        const { data: teamData, error: teamError } = await supabase
          .from("teams")
          .select(
            `
            *,
            teamassignments!inner (
              id,
              created,
              bowlerID,
              bowlers (
                id,
                name,
                usbcID,
                bowlersContact (
                  id,
                  type,
                  value,
                  isPrimary
                )
              )
            )
          `
          )
          .eq("id", teamId)
          .eq("isDeleted", false)
          .order("created", {
            referencedTable: "teamassignments",
            ascending: true,
          })
          .single();

        if (teamError) throw teamError;
        if (!teamData) {
          throw new Error("Team not found or has been deleted");
        }

        const { data: squadsData, error: squadsError } = await supabase
          .from("squads")
          .select(
            `
            id,
            squadDate,
            squadTime,
            bowlingCenters (
              id,
              centerName
            )
          `
          )
          .eq("tournamentID", tournamentId)
          .is("isDeleted", false)
          .order("squadDate", { ascending: true })
          .order("squadTime", { ascending: true });

        if (squadsError) throw squadsError;
        const eventsBySquad = await fetchEventsBySquad(
          (squadsData || []).map((squad) => squad.id)
        ).catch(() => ({}));
        setSquads(withSquadEventIds(squadsData || [], eventsBySquad));

        // Tournament-wide side pots and all squad copies. Filter by the
        // team's current squad when rendering so a squad move can remap.
        const { data: eventsData, error: eventsError } = await supabase
          .from("tournamentEvents")
          .select(
            `
            *,
            eventFee (
              id,
              fees (
                feeName
              ),
              amount
            )
          `
          )
          .eq("tournamentID", tournamentId)
          .eq("isPrimary", false)
          .eq("isDeleted", false);

        if (eventsError) throw eventsError;

        // Process side pot events to include entry fees
        const processedSidePotEvents =
          eventsData?.map((event) => {
            const fees = event.eventFee || [];
            const totalEntryFee = fees.reduce(
              (sum, fee) => sum + (fee.amount || 0),
              0
            );
            return {
              ...event,
              entryFee: totalEntryFee,
            };
          }) || [];

        setTournamentEvents(processedSidePotEvents);

        // Fetch all event configurations
        const { data: allEventConfigs, error: configError } = await supabase.rpc(
          "get_all_event_configurations_for_tournament",
          { input_tournament_id: tournamentId }
        );

        if (configError) {
          console.warn("Error loading all event configs:", configError);
        }

        // Transform all event configs to match expected format
        const transformedConfigs = (allEventConfigs || []).map((config) => ({
          allEventId: config.all_event_id,
          allEventName: config.all_event_name,
          primaryEventIds: config.primary_event_ids || [],
          secondaryRuleType: config.secondary_rule_type || "first_attempt",
        }));

        // Store in team data for later use
        teamData.allEventConfigs = transformedConfigs;

        // Fetch bowler averages
        const { data: averagesData, error: averagesError } = await supabase
          .from("tournamentAverages")
          .select("bowlerID, average, hasAverage")
          .eq("tournamentID", tournamentId);

        if (averagesError) throw averagesError;

        const { data: verificationStates, error: verificationError } =
          await supabase
            .from("verificationStates")
            .select("entityId, entityType, isVerified")
            .eq("tournamentId", tournamentId)
            .in("entityType", ["averages", "scores"]);

        if (verificationError) {
          console.warn(
            "Could not load verification states:",
            verificationError
          );
        }

        const scoresVerified = Boolean(
          verificationStates?.find(
            (vs) =>
              vs.entityType === "scores" &&
              vs.entityId === teamId &&
              vs.isVerified
          )
        );

        // Fetch paid events
        const { data: eventEntriesData, error: eventEntriesError } =
          await supabase.from("eventEntry").select("*").eq("teamID", teamId);

        if (eventEntriesError) throw eventEntriesError;

        // Process the data
        const processedBowlers = sortTeamAssignments(
          teamData.teamassignments
        ).map((assignment) => {
          const bowler = assignment.bowlers;
          const contactInfo = bowler.bowlersContact || [];

          // Extract phone and email from contact info
          const email =
            contactInfo.find((c) => c.type === "email" && c.isPrimary)?.value ||
            contactInfo.find((c) => c.type === "email")?.value ||
            "";
          const phone =
            contactInfo.find((c) => c.type === "phone" && c.isPrimary)?.value ||
            contactInfo.find((c) => c.type === "phone")?.value ||
            "";

          const averageData = averagesData.find(
            (avg) => avg.bowlerID === assignment.bowlerID
          );
          const hasAverage = averageData?.hasAverage !== false; // Default to true for backward compatibility
          const average = hasAverage ? averageData?.average || "" : null;

          const verification = verificationStates?.find(
            (vs) =>
              vs.entityType === "averages" &&
              vs.entityId === assignment.bowlerID
          );
          const isVerified = verification?.isVerified || false;
          const paidEvents = eventEntriesData
            .filter((entry) => entry.bowlerID === assignment.bowlerID)
            .map((entry) => entry.eventsID);

          // Convert paidEvents array to selectedSidePots object format
          const selectedSidePots = {};
          paidEvents.forEach((eventId) => {
            selectedSidePots[eventId] = true;
          });

          return {
            id: bowler.id,
            originalBowlerId: bowler.id,
            name: bowler.name,
            usbcID: bowler.usbcID || "",
            phone: phone,
            email: email,
            average: average,
            hasAverage,
            isVerified,
            paidEvents: paidEvents || [],
            selectedSidePots: selectedSidePots,
            bracketTickets: {},
            allEventStatus: null, // Will be loaded by useAllEventManagement
          };
        });

        const products = productsEnabledForSquad(
          (await listBracketProducts(tournamentId)) || [],
          teamData.squadID
        );
        const tickets = await listSquadTickets(
          teamData.squadID,
          products.map((product) => product.id)
        );
        processedBowlers.forEach((row) => {
          row.bracketTickets = countsFromTickets(
            tickets,
            products,
            teamData.squadID,
            row.id
          );
        });

        setTeam({ ...teamData, scoresVerified });
        setFormData({ bowlers: processedBowlers });
      } catch (error) {
        console.error("Error fetching team data:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      teamId,
      tournamentId,
      setLoading,
      setTeam,
      setSquads,
      setTournamentEvents,
      setFormData,
    ]
  );

  useEffect(() => {
    loadTeam().catch(() => {});
  }, [loadTeam]);

  const reloadTeam = useCallback(() => loadTeam({ silent: true }), [loadTeam]);

  return { reloadTeam };
};
